import React, {useEffect, useState} from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Grid, IconButton, MenuItem, TextField, Tooltip, Typography
} from "@material-ui/core";
import {AddRounded, DeleteRounded} from "@material-ui/icons";

/**
 * Alta / edición de un grupo de equivalencia.
 *
 * El grupo es un conjunto de códigos (marca + código). Se pueden agregar tantas filas
 * como haga falta, incluso varias de la misma marca, que es el caso de un proveedor
 * con más de un código para el mismo artículo.
 */
export const EquivalenciasEditDialog = ({open, grupo, marcas, onConfirm, onCancel}) => {
    const [codigos, setCodigos] = useState([]);
    const [observaciones, setObservaciones] = useState('');

    const esNuevo = !grupo;

    useEffect(() => {
        if (!open) return;

        if (grupo) {
            setCodigos(grupo.codigos.map(codigo => ({marca: codigo.marca, codigo: codigo.codigo})));
            setObservaciones(grupo.observaciones || '');
        } else {
            // Arranco con dos filas vacías: un grupo necesita al menos dos códigos.
            const marcaPorDefecto = marcas.length > 0 ? marcas[0].id : '';
            setCodigos([{marca: marcaPorDefecto, codigo: ''}, {marca: marcaPorDefecto, codigo: ''}]);
            setObservaciones('');
        }
    }, [open, grupo]);

    const onChangeCodigo = (index, campo, valor) => {
        setCodigos(codigos.map((codigo, i) => i === index ? {...codigo, [campo]: valor} : codigo));
    };

    const onAgregar = () => {
        const marcaPorDefecto = marcas.length > 0 ? marcas[0].id : '';
        setCodigos([...codigos, {marca: marcaPorDefecto, codigo: ''}]);
    };

    const onQuitar = index => setCodigos(codigos.filter((codigo, i) => i !== index));

    // Descarto las filas sin código antes de validar y guardar.
    const codigosCargados = codigos.filter(codigo => codigo.codigo.trim() !== '');
    const puedeGuardar = codigosCargados.length >= 2;

    return (
        <Dialog open={open} onClose={onCancel} maxWidth={"sm"} fullWidth>
            <DialogTitle>
                {esNuevo ? 'Nueva equivalencia' : `Editar equivalencia`}
                {!esNuevo && (
                    <Typography color={"textSecondary"} component={"span"}> #{grupo.id}</Typography>
                )}
            </DialogTitle>
            <DialogContent>
                <Typography variant={"caption"} color={"textSecondary"}>
                    Códigos equivalentes entre sí. Una misma marca puede tener más de un código.
                </Typography>

                <Box className={'mt-3'}>
                    {codigos.map((codigo, index) => (
                        <Grid container spacing={1} alignItems={"flex-end"} key={index} className={'mb-2'}>
                            <Grid item xs={4}>
                                <TextField
                                    select
                                    fullWidth
                                    label={index === 0 ? 'Marca' : ''}
                                    value={codigo.marca}
                                    onChange={event => onChangeCodigo(index, 'marca', event.target.value)}
                                >
                                    {marcas.map(marca => (
                                        <MenuItem key={marca.id} value={marca.id}>
                                            {marca.description}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={7}>
                                <TextField
                                    fullWidth
                                    label={index === 0 ? 'Código' : ''}
                                    value={codigo.codigo}
                                    onChange={event => onChangeCodigo(index, 'codigo', event.target.value)}
                                />
                            </Grid>
                            <Grid item xs={1}>
                                <Tooltip title={'Quitar'}>
                                    <span>
                                        <IconButton
                                            size={"small"}
                                            disabled={codigos.length <= 2}
                                            onClick={() => onQuitar(index)}
                                        >
                                            <DeleteRounded fontSize={"small"}/>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    ))}
                </Box>

                <Button
                    color={"primary"}
                    size={"small"}
                    startIcon={<AddRounded/>}
                    onClick={onAgregar}
                    className={'mt-1'}
                >
                    Agregar código
                </Button>

                <TextField
                    fullWidth
                    label={'Observaciones'}
                    value={observaciones}
                    onChange={event => setObservaciones(event.target.value)}
                    className={'mt-3'}
                    inputProps={{maxLength: 60}}
                />

                {!puedeGuardar && (
                    <Typography variant={"caption"} color={"error"} component={"div"} className={'mt-3'}>
                        Cargar al menos dos códigos para poder guardar.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancelar</Button>
                <Button
                    color={"primary"}
                    variant={"contained"}
                    disabled={!puedeGuardar}
                    onClick={() => onConfirm(codigosCargados, observaciones)}
                >
                    Guardar
                </Button>
            </DialogActions>
        </Dialog>
    );
};
